import { EmailChipsInput } from '@/components/ui/EmailChipsInput';
import { TimePicker } from '@/components/ui/TimePicker';
import {
  useAutomationConfigConverter,
  useExecuteConfigurationNow,
  useFolderAutomations,
  useSaveFolderAutomations,
} from '@/hooks/useAutomation';

import { useAuthStore } from '@/store/auth.store';
import { Folder } from '@/types/domain.types';
import {
  normalizeEmailFromArray,
  normalizeEmailToArray,
  validateEmails,
} from '@/utils/email.utils';
import {
  convertParisTimeToUserTimezone,
  getUserTimezone,
} from '@/utils/timezone';
import { Calendar, Clock, Play, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTOMATION_TEMPLATES } from '../constants/automation.constants';
import {
  AutomationConfig,
  EditionConfig,
  FolderAutomations,
} from '../types/automation.types';
import { AutomationHistory } from './AutomationHistory';
import { EditionManager } from './EditionManager';

interface AutomationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folder: Folder | undefined | null;
  onDataRefresh?: () => void;
}

export const AutomationDrawer = ({
  isOpen,
  onClose,
  folder,
  onDataRefresh,
}: AutomationDrawerProps) => {
  console.log({ folder });
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'configure' | 'history'>(
    'configure'
  );
  const [automations, setAutomations] = useState<FolderAutomations>({});
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(
    new Set()
  );
  // Editions state for multiple editions support
  const [editions, setEditions] = useState<Record<string, EditionConfig[]>>({});

  // React Query hooks
  const {
    data: existingConfigs,
    isLoading: isLoadingConfigs,
    refetch: refetchConfigs,
  } = useFolderAutomations(folder?.uuid || '');
  const saveMutation = useSaveFolderAutomations();
  const executeNowMutation = useExecuteConfigurationNow();
  const { convertToApiFormat, convertFromApiFormat } =
    useAutomationConfigConverter();

  // Refetch data when drawer opens
  useEffect(() => {
    if (isOpen && folder?.uuid) {
      refetchConfigs();
    }
  }, [isOpen, folder?.uuid, refetchConfigs]);

  // Load existing configurations when drawer opens or data changes
  useEffect(() => {
    if (isOpen && folder && existingConfigs && existingConfigs.length > 0) {
      console.log('📥 Existing configs from API:', existingConfigs);
      const convertedConfigs = convertFromApiFormat(existingConfigs);
      console.log('🔄 Converted configs:', convertedConfigs);

      // Group edition configs by their base template
      const groupedConfigs: FolderAutomations = {};
      const editionConfigs: EditionConfig[] = [];

      for (const [key, config] of Object.entries(convertedConfigs)) {
        // Un type qui n'est plus proposé (ex. « Demande personnalisée », retiré
        // le 10/09/2026) n'est ni affiché ni renvoyé : l'API supprime à la
        // sauvegarde ce qui n'est plus dans la liste.
        if (!AUTOMATION_TEMPLATES.some(t => t.id === config.templateId)) {
          continue;
        }
        if (config.templateId === 'generate-edition') {
          // Convert edition config to EditionConfig format
          const editionConfig: EditionConfig = {
            id: `edition-${Date.now()}-${Math.random()}-${editionConfigs.length}`,
            editionOptionValue: config.editionOptionValue || '',
            editionOptionName: config.editionOptionName || '',
            editionEmail: config.editionEmail || '',
            dayOfMonth: config.dayOfMonth,
            executionHour: config.executionHour,
            executionMinute: config.executionMinute,
            usePreviousMonth: config.usePreviousMonth ?? true,
          };
          editionConfigs.push(editionConfig);
        } else {
          groupedConfigs[key] = config;
        }
      }

      // If we have edition configs, group them under 'generate-edition'
      if (editionConfigs.length > 0) {
        console.log(
          `Found ${editionConfigs.length} edition configs:`,
          editionConfigs
        );
        const firstEditionConfig = editionConfigs[0];
        // Get the first converted config to retrieve notificationEmail
        const firstConvertedConfig = Object.values(convertedConfigs).find(
          c => c.templateId === 'generate-edition'
        );
        groupedConfigs['generate-edition'] = {
          templateId: 'generate-edition',
          email: firstEditionConfig.editionEmail || folder?.adresseEmail || '',
          dayOfMonth: 1, // Default value, will be overridden by individual editions
          notificationEmail:
            firstConvertedConfig?.notificationEmail || user?.email || '',
          executionHour: 10, // Default value, will be overridden by individual editions
          executionMinute: 0, // Default value, will be overridden by individual editions
          retryHour: 16,
          retryMinute: 0,
          // Remove edition-specific fields from the base config
          editionOptionValue: undefined,
          editionOptionName: undefined,
          editionEmail: undefined,
        };

        // Store the editions separately
        setEditions(prev => ({
          ...prev,
          'generate-edition': editionConfigs,
        }));
      }

      console.log('✅ Final grouped configs:', groupedConfigs);
      setAutomations(groupedConfigs);
      setSelectedTemplates(new Set(Object.keys(groupedConfigs)));
    } else if (
      isOpen &&
      folder &&
      (!existingConfigs || existingConfigs.length === 0)
    ) {
      // Reset form when drawer opens but no existing configs
      setAutomations({});
      setSelectedTemplates(new Set());
      setEditions({});
    }
  }, [isOpen, folder, existingConfigs, convertFromApiFormat, user?.email]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('configure');
      // setAutomations({});
      // setSelectedTemplates(new Set());
      // setEditions({});
    }
  }, [isOpen]);

  // Initialize editions when template is selected
  useEffect(() => {
    if (
      selectedTemplates.has('generate-edition') &&
      !editions['generate-edition']
    ) {
      setEditions(prev => ({
        ...prev,
        ['generate-edition']: [],
      }));
    }
  }, [selectedTemplates, editions]);

  const handleTemplateToggle = (templateId: string) => {
    const newSelectedTemplates = new Set(selectedTemplates);

    if (newSelectedTemplates.has(templateId)) {
      // Remove template
      newSelectedTemplates.delete(templateId);
      const newAutomations = { ...automations };
      delete newAutomations[templateId];
      setAutomations(newAutomations);

      // Remove editions for this template
      const newEditions = { ...editions };
      delete newEditions[templateId];
      setEditions(newEditions);
    } else {
      // Add template with default config
      newSelectedTemplates.add(templateId);
      setAutomations(prev => ({
        ...prev,
        [templateId]: {
          templateId,
          email: folder?.adresseEmail || '',
          dayOfMonth: 1,
          notificationEmail: user?.email || '',
          sendToEmployeePortal: false,
          sendToManagerPortal: false,
          // New execution time fields with defaults
          executionHour: 10,
          executionMinute: 0,
          retryHour: 16,
          retryMinute: 0,
          // DSN specific fields
          dsnOption: 'calculate-only',
          sendDsnByEmail: false,
          dsnEmail: folder?.adresseEmail || '',
          generateBulletinOption: 'generate-only',
          customDescription: '',
          // Demande de variables : le lien ouvre tout, sauf réglage contraire
          collecteCanal: 'les_deux',
          // Edition defaults (legacy support)
          editionOptionValue: '',
          editionOptionName: '',
          editionEmail: folder?.adresseEmail || '',
        },
      }));

      // Initialize empty editions array for generate-edition template
      if (templateId === 'generate-edition') {
        setEditions(prev => ({
          ...prev,
          [templateId]: [],
        }));
      }
    }

    setSelectedTemplates(newSelectedTemplates);
  };

  const handleConfigChange = (
    templateId: string,
    field: keyof AutomationConfig,
    value: AutomationConfig[keyof AutomationConfig]
  ) => {
    setAutomations(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        [field]: value,
      },
    }));
  };

  const handleEditionsChange = (
    templateId: string,
    newEditions: EditionConfig[]
  ) => {
    setEditions(prev => ({
      ...prev,
      [templateId]: newEditions,
    }));

    // Update the automation config with the new editions
    setAutomations(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        editions: newEditions,
      },
    }));
  };

  const handleSave = async () => {
    if (!folder) return;

    try {
      // Create separate automation configs for each edition
      const allConfigs = [];

      for (const [templateId, config] of Object.entries(automations)) {
        if (
          templateId === 'generate-edition' &&
          config.editions &&
          config.editions.length > 0
        ) {
          // Create one automation config per edition
          for (const edition of config.editions) {
            const editionConfig = {
              ...config,
              templateId: 'generate-edition',
              // Override with edition-specific values
              dayOfMonth: edition.dayOfMonth,
              executionHour: edition.executionHour,
              executionMinute: edition.executionMinute,
              editionOptionValue: edition.editionOptionValue,
              editionOptionName: edition.editionOptionName,
              editionEmail: edition.editionEmail,
              usePreviousMonth: edition.usePreviousMonth ?? true,
              // Remove the editions array as it's not needed in the API
              editions: undefined,
            };
            allConfigs.push(editionConfig);
          }
        } else {
          // For non-edition templates, use the config as-is
          allConfigs.push(config);
        }
      }

      const apiConfigs = convertToApiFormat(
        folder.uuid,
        folder.domainUuid,
        allConfigs.reduce((acc, config, index) => {
          acc[`${config.templateId}-${index}`] = config;
          return acc;
        }, {} as FolderAutomations)
      );

      await saveMutation.mutateAsync({
        folderUuid: folder.uuid,
        configurations: apiConfigs,
      });

      // Refetch fresh data after successful save
      await refetchConfigs();

      // Refresh parent data if callback provided
      onDataRefresh?.();

      onClose();
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error('Failed to save automation configs:', error);
    }
  };

  const isConfigValid = (templateId: string) => {
    const config = automations[templateId];
    if (!config) return false;

    const template = AUTOMATION_TEMPLATES.find(t => t.id === templateId);
    if (!template) return false;

    // Check required fields
    if (
      template.requiredFields.includes('email') &&
      config.generateBulletinOption !== 'generate-only'
    ) {
      const emails = normalizeEmailToArray(config.email);
      if (emails.length === 0 || !validateEmails(emails)) {
        console.log('email is required and must be valid');
        return false;
      }
    }
    // Check DSN email if sendDsnByEmail is enabled
    if (templateId === 'calculate-dsn' && config.sendDsnByEmail) {
      const dsnEmails = normalizeEmailToArray(config.dsnEmail);
      if (dsnEmails.length === 0 || !validateEmails(dsnEmails)) {
        console.log('dsnEmail is required when sendDsnByEmail is enabled');
        return false;
      }
    }

    // Notification email is required
    const notificationEmails = normalizeEmailToArray(config.notificationEmail);
    if (
      notificationEmails.length === 0 ||
      !validateEmails(notificationEmails)
    ) {
      console.log('notificationEmail is required and must be valid');
      return false;
    }

    // Edition-specific validations
    if (templateId === 'generate-edition') {
      // Check if we have editions configured
      const templateEditions = editions[templateId] || [];
      if (templateEditions.length === 0) return false;

      // Validate each edition
      for (const edition of templateEditions) {
        if (!edition.editionOptionValue) return false;
        if (
          !edition.dayOfMonth ||
          edition.dayOfMonth < 1 ||
          edition.dayOfMonth > 31
        )
          return false;
        if (edition.executionHour < 0 || edition.executionHour > 23)
          return false;
        if (edition.executionMinute < 0 || edition.executionMinute > 59)
          return false;
        // Validate edition emails if provided
        if (edition.editionEmail) {
          const editionEmails = normalizeEmailToArray(edition.editionEmail);
          if (editionEmails.length > 0 && !validateEmails(editionEmails)) {
            return false;
          }
        }
      }
    }

    return true;
  };

  const canSave = () => {
    if (selectedTemplates.size === 0) return true;
    return Array.from(selectedTemplates).every(templateId => {
      console.log({ templateId });
      return isConfigValid(templateId);
    });
  };

  // Get configuration UUID for a template from existing configs
  const getConfigurationUuid = (templateId: string): string | null => {
    console.log({ existingConfigs });
    if (!existingConfigs) return null;
    const config = existingConfigs.find(c => c.templateId === templateId);
    return config?.uuid || null;
  };

  // Execute configuration immediately
  const handleExecuteNow = async (templateId: string) => {
    if (!folder?.uuid) return;

    const configurationUuid = getConfigurationUuid(templateId);
    if (!configurationUuid) return;

    try {
      await executeNowMutation.mutateAsync({
        folderUuid: folder.uuid,
        configurationUuid,
      });
    } catch (error) {
      console.error('Failed to execute configuration:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="border-b border-neutral-200">
            <div className="flex items-center justify-between p-6 pb-0">
              <div>
                <h2 className="text-xl font-bold text-neutral-900">
                  {t('automation.title', 'Folder Automation')}
                </h2>
                <p className="text-sm text-neutral-600">{folder?.name || ''}</p>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex">
              <button
                onClick={() => setActiveTab('configure')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'configure'
                    ? 'border-primary-600 text-primary-600 border-b-2'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                {t('automation.tabs.configure', 'Configure')}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'border-primary-600 text-primary-600 border-b-2'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                {t('automation.tabs.history', 'History')}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'configure' ? (
              <div className="space-y-6">
                {isLoadingConfigs ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-neutral-500">
                      {t('common.loading', 'Loading...')}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Template Selection and Configuration */}
                    <div>
                      <h3 className="mb-4 text-lg font-semibold text-neutral-900">
                        {t('automation.templates.title', 'Choose Templates')}
                      </h3>
                      <p className="mb-6 text-sm text-neutral-600">
                        {t(
                          'automation.templates.description',
                          'Vous pouvez sélectionner plusieurs automatisations pour ce dossier.'
                        )}
                      </p>
                      <div className="space-y-4">
                        {AUTOMATION_TEMPLATES.map(template => {
                          const Icon = template.icon;
                          const isSelected = selectedTemplates.has(template.id);
                          const config = automations[template.id];

                          return (
                            <div key={template.id} className="space-y-4">
                              {/* Template Selection Block */}
                              <button
                                onClick={() =>
                                  handleTemplateToggle(template.id)
                                }
                                className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                                  isSelected
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <div
                                    className={`rounded-lg p-2 ${
                                      isSelected
                                        ? 'bg-primary-100 text-primary-600'
                                        : 'bg-neutral-100 text-neutral-600'
                                    }`}
                                  >
                                    <Icon className="h-5 w-5" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h4 className="font-semibold text-neutral-900">
                                      {t(template.nameKey)}
                                    </h4>
                                    <p className="text-sm text-neutral-600">
                                      {t(template.descriptionKey)}
                                    </p>
                                  </div>
                                  {isSelected && (
                                    <div className="bg-primary-600 flex h-5 w-5 items-center justify-center rounded-full">
                                      <div className="h-2 w-2 rounded-full bg-white"></div>
                                    </div>
                                  )}
                                </div>
                              </button>

                              {/* Configuration Form - appears directly below when selected */}
                              {isSelected && config && (
                                <div className="ml-4 space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                                  <div className="flex items-center justify-between border-b border-neutral-200 pb-2">
                                    <div className="flex items-center gap-2">
                                      <Icon className="text-primary-600 h-4 w-4" />
                                      <h4 className="font-medium text-neutral-900">
                                        {t(
                                          'automation.configuration.title',
                                          'Configuration'
                                        )}{' '}
                                        - {t(template.nameKey)}
                                      </h4>
                                    </div>

                                    {/* Execute Now Button - only show for existing configurations */}
                                    {getConfigurationUuid(template.id) && (
                                      <button
                                        onClick={() =>
                                          handleExecuteNow(template.id)
                                        }
                                        disabled={executeNowMutation.isPending}
                                        className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        <Play className="h-3 w-3" />
                                        {executeNowMutation.isPending
                                          ? t(
                                              'automations.execution.executing',
                                              'Executing...'
                                            )
                                          : t(
                                              'automations.execution.executeNow',
                                              'Execute now'
                                            )}
                                      </button>
                                    )}
                                  </div>

                                  <div className="space-y-4">
                                    {template.requiredFields.includes(
                                      'dayOfMonth'
                                    ) &&
                                      template.id !== 'generate-edition' && (
                                        <div>
                                          <label className="mb-2 block text-sm font-medium text-neutral-900">
                                            <Calendar className="mr-2 inline h-4 w-4" />
                                            {t(
                                              'automation.dayOfMonth',
                                              'Day of Month'
                                            )}
                                          </label>
                                          <select
                                            value={config.dayOfMonth}
                                            onChange={e =>
                                              handleConfigChange(
                                                template.id,
                                                'dayOfMonth',
                                                parseInt(e.target.value)
                                              )
                                            }
                                            className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:ring-2"
                                          >
                                            {Array.from(
                                              {
                                                length:
                                                  template.id ===
                                                  'calculate-dsn'
                                                    ? 14
                                                    : template.id ===
                                                        'generate-bulletin'
                                                      ? 13
                                                      : template.id ===
                                                          'generate-edition'
                                                        ? 31
                                                        : 28,
                                              },
                                              (_, i) =>
                                                template.id ===
                                                'generate-bulletin'
                                                  ? i + 16
                                                  : template.id ===
                                                      'generate-edition'
                                                    ? i + 1
                                                    : i + 1
                                            ).map(day => (
                                              <option key={day} value={day}>
                                                {day}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      )}
                                    {/* Calculate bulletin specific options */}
                                    {template.id === 'generate-bulletin' && (
                                      <div>
                                        <label className="mb-2 block text-sm font-medium text-neutral-900">
                                          {t(
                                            'automation.bulletin.option',
                                            'Generate Bulletin Option'
                                          )}
                                        </label>
                                        <div className="space-y-2">
                                          <label className="flex items-center gap-3">
                                            <input
                                              type="radio"
                                              value="generate-only"
                                              checked={
                                                config.generateBulletinOption ===
                                                'generate-only'
                                              }
                                              onChange={e =>
                                                handleConfigChange(
                                                  template.id,
                                                  'generateBulletinOption',
                                                  e.target.value
                                                )
                                              }
                                              className="text-primary-600 h-4 w-4"
                                            />
                                            <span className="text-sm text-neutral-900">
                                              {t(
                                                'automation.bulletin.generateOnly',
                                                'Generate bulletin Only'
                                              )}
                                            </span>
                                          </label>
                                          <label className="flex items-center gap-3">
                                            <input
                                              type="radio"
                                              value="generate-and-send"
                                              checked={
                                                config.generateBulletinOption ===
                                                'generate-and-send'
                                              }
                                              onChange={e =>
                                                handleConfigChange(
                                                  template.id,
                                                  'generateBulletinOption',
                                                  e.target.value
                                                )
                                              }
                                              className="text-primary-600 h-4 w-4"
                                            />
                                            <span className="text-sm text-neutral-900">
                                              {t(
                                                'automation.bulletin.generateAndSend',
                                                'Generate bulletin and send'
                                              )}
                                            </span>
                                          </label>
                                        </div>
                                      </div>
                                    )}
                                    {template.id === 'generate-bulletin' && (
                                      <div className="space-y-2">
                                        <p className="text-sm font-medium text-neutral-900">
                                          {t(
                                            'automation.bulletin.portal.title',
                                            'Portal delivery'
                                          )}
                                        </p>
                                        <p className="text-xs text-neutral-500">
                                          {t(
                                            'automation.bulletin.portal.description',
                                            'Decide whether bulletins are published to employee and manager portals.'
                                          )}
                                        </p>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                                          <label className="flex items-center gap-3 text-sm text-neutral-900">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(
                                                config.sendToEmployeePortal
                                              )}
                                              onChange={e =>
                                                handleConfigChange(
                                                  template.id,
                                                  'sendToEmployeePortal',
                                                  e.target.checked
                                                )
                                              }
                                              className="text-primary-600 h-4 w-4"
                                            />
                                            {t(
                                              'automation.bulletin.portal.employee',
                                              'Send to employee portal'
                                            )}
                                          </label>
                                          <label className="flex items-center gap-3 text-sm text-neutral-900">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(
                                                config.sendToManagerPortal
                                              )}
                                              onChange={e =>
                                                handleConfigChange(
                                                  template.id,
                                                  'sendToManagerPortal',
                                                  e.target.checked
                                                )
                                              }
                                              className="text-primary-600 h-4 w-4"
                                            />
                                            {t(
                                              'automation.bulletin.portal.manager',
                                              'Send to manager portal'
                                            )}
                                          </label>
                                        </div>
                                      </div>
                                    )}
                                    {/* Generate edition specific options */}
                                    {template.id === 'generate-edition' && (
                                      <div className="space-y-4">
                                        <EditionManager
                                          folderUuid={folder?.uuid || ''}
                                          editions={editions[template.id] || []}
                                          onEditionsChange={newEditions =>
                                            handleEditionsChange(
                                              template.id,
                                              newEditions
                                            )
                                          }
                                          defaultEmail={
                                            folder?.adresseEmail || ''
                                          }
                                        />
                                      </div>
                                    )}
                                    {/* Execution Time Configuration - only for non-edition templates */}
                                    {template.requiredFields.includes(
                                      'executionHour'
                                    ) &&
                                      template.id !== 'generate-edition' && (
                                        <div className="space-y-4">
                                          <div className="border-t border-neutral-200 pt-4">
                                            <h5 className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-900">
                                              <Clock className="h-4 w-4" />
                                              {t(
                                                'automation.executionTime.title',
                                                "Heures d'exécution"
                                              )}
                                            </h5>
                                            <p className="mb-2 text-xs text-neutral-500">
                                              {t(
                                                'automation.executionTime.description',
                                                "Configurez les heures d'exécution de vos automatisations"
                                              )}
                                            </p>
                                            <p className="mb-4 rounded-lg bg-neutral-100 p-2 text-xs text-neutral-600">
                                              <strong>
                                                {t(
                                                  'automation.executionTime.timezoneNote',
                                                  'Note:'
                                                )}
                                              </strong>{' '}
                                              {t(
                                                'automation.executionTime.timezoneExplanation',
                                                "Les heures sont définies en timezone Paris (Europe/Paris). L'équivalent dans votre timezone locale sera affiché ci-dessous."
                                              )}
                                            </p>

                                            <TimePicker
                                              hour={config.executionHour}
                                              minute={config.executionMinute}
                                              onHourChange={hour =>
                                                handleConfigChange(
                                                  template.id,
                                                  'executionHour',
                                                  hour
                                                )
                                              }
                                              onMinuteChange={minute =>
                                                handleConfigChange(
                                                  template.id,
                                                  'executionMinute',
                                                  minute
                                                )
                                              }
                                              label={t(
                                                'automation.executionTime.executionTime',
                                                "Heure d'exécution principale"
                                              )}
                                            />

                                            {/* Timezone equivalent display */}
                                            <div className="bg-primary-50 mt-3 hidden rounded-lg p-3">
                                              <div className="flex items-center gap-2 text-sm">
                                                <Clock className="text-primary-600 h-4 w-4" />
                                                <span className="text-primary-900 font-medium">
                                                  {t(
                                                    'automation.executionTime.equivalentInYourTimezone',
                                                    'Équivalent dans votre timezone:'
                                                  )}
                                                </span>
                                              </div>
                                              <div className="text-primary-700 mt-1 text-sm">
                                                {(() => {
                                                  const userTimezone =
                                                    getUserTimezone(
                                                      user?.timezone
                                                    );
                                                  const timeConversion =
                                                    convertParisTimeToUserTimezone(
                                                      config.executionHour,
                                                      config.executionMinute,
                                                      userTimezone
                                                    );
                                                  return timeConversion.displayText;
                                                })()}
                                              </div>
                                            </div>

                                            {/* <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                              <label className="mb-2 block text-xs font-medium text-neutral-700">
                                                {t(
                                                  'automation.executionTime.retryHour',
                                                  "Heure de retry en cas d'échec"
                                                )}
                                              </label>
                                              <select
                                                value={config.retryHour}
                                                onChange={e =>
                                                  handleConfigChange(
                                                    template.id,
                                                    'retryHour',
                                                    parseInt(e.target.value)
                                                  )
                                                }
                                                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                                              >
                                                {Array.from(
                                                  { length: 24 },
                                                  (_, i) => (
                                                    <option key={i} value={i}>
                                                      {i
                                                        .toString()
                                                        .padStart(2, '0')}
                                                      h
                                                    </option>
                                                  )
                                                )}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="mb-2 block text-xs font-medium text-neutral-700">
                                                {t(
                                                  'automation.executionTime.retryMinute',
                                                  "Minute de retry en cas d'échec"
                                                )}
                                              </label>
                                              <select
                                                value={config.retryMinute}
                                                onChange={e =>
                                                  handleConfigChange(
                                                    template.id,
                                                    'retryMinute',
                                                    parseInt(e.target.value)
                                                  )
                                                }
                                                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                                              >
                                                {Array.from(
                                                  { length: 60 },
                                                  (_, i) => (
                                                    <option key={i} value={i}>
                                                      {i
                                                        .toString()
                                                        .padStart(2, '0')}
                                                      min
                                                    </option>
                                                  )
                                                )}
                                              </select>
                                            </div>
                                          </div> */}
                                          </div>
                                        </div>
                                      )}

                                    {/* Common fields */}
                                    {template.requiredFields.includes(
                                      'email'
                                    ) &&
                                      template.id === 'generate-bulletin' &&
                                      config.generateBulletinOption ===
                                        'generate-and-send' && (
                                        <EmailChipsInput
                                          emails={normalizeEmailToArray(
                                            config.email
                                          )}
                                          onChange={emails =>
                                            handleConfigChange(
                                              template.id,
                                              'email',
                                              normalizeEmailFromArray(emails)
                                            )
                                          }
                                          label={t(
                                            'automation.email',
                                            'Email Address'
                                          )}
                                          placeholder="email@example.com"
                                          description={t(
                                            'automation.email.description',
                                            'Destinataires des bulletins générés (plusieurs emails possibles)'
                                          )}
                                        />
                                      )}
                                    {/* Demande de variables : à qui part le lien, et ce qu'il ouvre */}
                                    {template.id === 'demande-variables' && (
                                      <>
                                        <EmailChipsInput
                                          emails={normalizeEmailToArray(
                                            config.email
                                          )}
                                          onChange={emails =>
                                            handleConfigChange(
                                              template.id,
                                              'email',
                                              normalizeEmailFromArray(emails)
                                            )
                                          }
                                          label="Adresse du client"
                                          placeholder="Adresse du client"
                                          description="Laissez vide pour utiliser l’adresse du dossier."
                                        />
                                        {/* Décision Rose du 21/09/2026 : trois choix, le mail
                                            contient toujours le lien. */}
                                        <div>
                                          <label className="mb-2 block text-sm font-medium text-neutral-900">
                                            Ce que le lien ouvre
                                          </label>
                                          <div className="space-y-2">
                                            {(
                                              [
                                                {
                                                  valeur: 'les_deux',
                                                  libelle:
                                                    'Un message avec pièces jointes, et la saisie des absences et variables',
                                                },
                                                {
                                                  valeur: 'reponse',
                                                  libelle:
                                                    'Un message avec pièces jointes seulement',
                                                },
                                                {
                                                  valeur: 'saisie',
                                                  libelle:
                                                    'La saisie des absences et variables seulement',
                                                },
                                              ] as const
                                            ).map(choix => (
                                              <label
                                                key={choix.valeur}
                                                className="flex items-center gap-3"
                                              >
                                                <input
                                                  type="radio"
                                                  value={choix.valeur}
                                                  checked={
                                                    (config.collecteCanal ??
                                                      'les_deux') ===
                                                    choix.valeur
                                                  }
                                                  onChange={e =>
                                                    handleConfigChange(
                                                      template.id,
                                                      'collecteCanal',
                                                      e.target.value
                                                    )
                                                  }
                                                  className="text-primary-600 h-4 w-4"
                                                />
                                                <span className="text-sm text-neutral-900">
                                                  {choix.libelle}
                                                </span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                    {/* DSN specific options */}
                                    {template.id === 'calculate-dsn' && (
                                      <div className="space-y-4">
                                        <div>
                                          <label className="mb-2 block text-sm font-medium text-neutral-900">
                                            {t(
                                              'automation.dsn.option',
                                              'DSN Option'
                                            )}
                                          </label>
                                          <div className="space-y-2">
                                            <label className="flex items-center gap-3">
                                              <input
                                                type="radio"
                                                value="calculate-only"
                                                checked={
                                                  config.dsnOption ===
                                                  'calculate-only'
                                                }
                                                onChange={e =>
                                                  handleConfigChange(
                                                    template.id,
                                                    'dsnOption',
                                                    e.target.value
                                                  )
                                                }
                                                className="text-primary-600 h-4 w-4"
                                              />
                                              <span className="text-sm text-neutral-900">
                                                {t(
                                                  'automation.dsn.calculateDsn',
                                                  'Calculate DSN'
                                                )}
                                              </span>
                                            </label>
                                            <label className="flex items-center gap-3">
                                              <input
                                                type="radio"
                                                value="calculate-and-send"
                                                checked={
                                                  config.dsnOption ===
                                                  'calculate-and-send'
                                                }
                                                onChange={e =>
                                                  handleConfigChange(
                                                    template.id,
                                                    'dsnOption',
                                                    e.target.value
                                                  )
                                                }
                                                className="text-primary-600 h-4 w-4"
                                              />
                                              <span className="text-sm text-neutral-900">
                                                {t(
                                                  'automation.dsn.calculateAndSend',
                                                  'Calculate DSN and Send to net entreprise'
                                                )}
                                              </span>
                                            </label>
                                          </div>
                                        </div>

                                        {/* DSN Email Configuration */}
                                        <div className="border-t border-neutral-200 pt-4">
                                          <label className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-900">
                                            <input
                                              type="checkbox"
                                              checked={
                                                config.sendDsnByEmail || false
                                              }
                                              onChange={e =>
                                                handleConfigChange(
                                                  template.id,
                                                  'sendDsnByEmail',
                                                  e.target.checked
                                                )
                                              }
                                              className="text-primary-600 h-4 w-4"
                                            />
                                            {t(
                                              'automation.dsn.sendByEmail',
                                              'Envoyer DSN par email'
                                            )}
                                          </label>

                                          {config.sendDsnByEmail && (
                                            <div className="mt-3">
                                              <EmailChipsInput
                                                emails={normalizeEmailToArray(
                                                  config.dsnEmail
                                                )}
                                                onChange={emails =>
                                                  handleConfigChange(
                                                    template.id,
                                                    'dsnEmail',
                                                    normalizeEmailFromArray(
                                                      emails
                                                    )
                                                  )
                                                }
                                                label={t(
                                                  'automation.dsn.emailAddress',
                                                  "Adresse email pour l'envoi DSN"
                                                )}
                                                placeholder="dsn@entreprise.com"
                                                description={t(
                                                  'automation.dsn.emailDescription',
                                                  'Destinataires du DSN (plusieurs emails possibles)'
                                                )}
                                                className="text-xs"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Notification Email */}
                                    <EmailChipsInput
                                      emails={normalizeEmailToArray(
                                        config.notificationEmail
                                      )}
                                      onChange={emails =>
                                        handleConfigChange(
                                          template.id,
                                          'notificationEmail',
                                          normalizeEmailFromArray(emails)
                                        )
                                      }
                                      label={t(
                                        'automation.notificationEmail',
                                        'Notification Email (for failures)'
                                      )}
                                      placeholder="notifications@example.com"
                                      description={t(
                                        'automation.notificationEmail.description',
                                        "Destinataires des notifications d'échec (plusieurs emails possibles)"
                                      )}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <AutomationHistory
                folder={folder}
                configurations={folder?.automationConfigurations || []}
                executions={[]}
              />
            )}
          </div>

          {/* Footer */}
          {activeTab === 'configure' && !isLoadingConfigs && (
            <div className="border-t border-neutral-200 p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-neutral-600">
                  {selectedTemplates.size > 0 && (
                    <span>
                      {selectedTemplates.size}{' '}
                      {t(
                        'automation.templates.selected',
                        'template(s) selected'
                      )}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={onClose}
                    className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!canSave() || saveMutation.isPending}
                    className="bg-primary-600 hover:bg-primary-700 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saveMutation.isPending
                      ? t('common.loading', 'Loading...')
                      : t('common.save', 'Save')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
